import {
  FaActionTracker,
  StructuredLogger,
} from '@brighthub/structured-logging';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { CarePlanAnalytics } from '../analytics/care-plan-analytics';
import { CARE_PLAN_CLIENT_CONTEXT } from '../care-plan-client-context.token';
import { OPTION_API, OptionApi } from './api/option-api.token';
import {
  CarePlanOption,
  CarePlanOptionTitle,
  CreateOptionValue,
  KNOWN_OPTION_STATUS,
  OptionReferenceValue,
  UpdateOptionValue,
} from './options.model';
import { searchOptionTitles } from './option-title.catalog';
import {
  OPTION_REFERENCE_API,
  OptionReferenceApi,
} from './reference/option-reference-api.token';

@Injectable()
export class CarePlanOptionsStore {
  private readonly _actionLogs = new FaActionTracker(inject(StructuredLogger));
  private readonly _api = inject<OptionApi>(OPTION_API);
  private readonly _analytics = inject(CarePlanAnalytics);
  private readonly _clientContext = inject(CARE_PLAN_CLIENT_CONTEXT, {
    optional: true,
  });
  private readonly _references =
    inject<OptionReferenceApi>(OPTION_REFERENCE_API);
  private readonly _options = signal<CarePlanOption[]>([]);
  private readonly _optionTypes = signal<OptionReferenceValue[]>([]);
  private readonly _optionStatuses = signal<OptionReferenceValue[]>([]);
  private readonly _optionTitles = signal<CarePlanOptionTitle[]>([]);
  private readonly _allowedTransitions = signal<
    Readonly<Record<string, readonly string[]>>
  >({});
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);
  private readonly _savingIds = signal<ReadonlySet<string>>(new Set());
  private _caseId: string | null = null;
  private _loadGeneration = 0;
  private _referencesLoaded = false;
  private _referenceClientId: number | null | undefined;

  readonly options = this._options.asReadonly();
  readonly optionTypes = this._optionTypes.asReadonly();
  readonly optionStatuses = this._optionStatuses.asReadonly();
  readonly optionTitles = this._optionTitles.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly count = computed(() => this._options().length);
  readonly capabilities = this._references.capabilities;

  isSaving(optionId: string): boolean {
    return this._savingIds().has(optionId);
  }

  async load(caseId: string): Promise<void> {
    if (!caseId) return;
    this._caseId = caseId;
    const generation = ++this._loadGeneration;
    this._loading.set(true);
    this._error.set(null);
    const referenceLoad = this._loadReferences().catch(() => undefined);
    try {
      const options = await firstValueFrom(this._api.listOptions(caseId));
      if (generation !== this._loadGeneration || this._caseId !== caseId)
        return;
      this._options.set(options);
    } catch (error) {
      if (generation !== this._loadGeneration || this._caseId !== caseId)
        return;
      this._error.set(this._message(error));
    } finally {
      if (generation === this._loadGeneration && this._caseId === caseId) {
        this._loading.set(false);
      }
    }
    await referenceLoad;
  }

  retry(): Promise<void> {
    return this._caseId ? this.load(this._caseId) : Promise.resolve();
  }

  searchTitles(query: string): CarePlanOptionTitle[] {
    return searchOptionTitles(this._optionTitles(), query);
  }

  optionTypeLabel(code: string): string {
    return this._label(this._optionTypes(), code);
  }

  statusLabel(code: string): string {
    return this._label(this._optionStatuses(), code);
  }

  statusOptionsFor(statusCode: string): OptionReferenceValue[] {
    const codes = [
      statusCode,
      ...(this._allowedTransitions()[statusCode] ?? []),
    ].filter((code) => code !== KNOWN_OPTION_STATUS.IDENTIFIED);
    return [...new Set(codes)].map((code) => ({
      code,
      label: this.statusLabel(code),
    }));
  }

  requiresQualification(optionTitle: string): boolean {
    return this._titleReference(optionTitle)?.requiresQualification ?? false;
  }

  qualificationPrompt(
    optionTitle: string,
    optionId: string,
    caseId: string,
  ): string | null {
    const reference = this._titleReference(optionTitle);
    if (!reference?.requiresQualification) return null;
    return (
      reference.qualificationPrompt ||
      `Call the option activation skill and reference Option Id ${optionId}. The client wants to pursue this option to support their Case Id ${caseId}.`
    );
  }

  private _titleReference(optionTitle: string): CarePlanOptionTitle | null {
    const comparableTitle = optionTitle.trim().toLocaleLowerCase();
    return (
      this._optionTitles().find(
        (candidate) =>
          candidate.title.trim().toLocaleLowerCase() === comparableTitle,
      ) ?? null
    );
  }

  async create(
    value: CreateOptionValue,
    qualificationOverride = false,
  ): Promise<CarePlanOption> {
    return this._actionLogs.run(
      qualificationOverride
        ? 'care_plan.option.override_qualification'
        : 'care_plan.option.create',
      `${this._clientContext?.activeGuid()}:${this._caseId}`,
      async (logAttempt) => {
        logAttempt.identifiers = {
          client_guid: this._clientContext?.activeGuid(),
          case_id: this._caseId,
        };
        logAttempt.stage = 'save';
        const caseId = this._requiredCaseId();
        const clientGuid = this._clientContext?.activeGuid();
        const option = await firstValueFrom(
          logAttempt.request(this._api.createOption(caseId, value)),
        );
        logAttempt.completedCount = 1;
        if (clientGuid) {
          this._analytics.optionCreated({
            clientGuid,
            caseId,
            optionId: option.id,
            optionType: option.optionTypeCode,
            initialStatus: option.statusCode,
            sourceType: option.sourceTypeCode,
          });
        }
        await this.load(caseId);
        return option;
      },
    );
  }

  async update(
    optionId: string,
    value: UpdateOptionValue,
    qualificationOverride = false,
  ): Promise<CarePlanOption> {
    return this._actionLogs.run(
      qualificationOverride
        ? 'care_plan.option.override_qualification'
        : value.statusCode !== undefined
          ? 'care_plan.option.change_status'
          : 'care_plan.option.update',
      `${this._clientContext?.activeGuid()}:${this._caseId}:${optionId}`,
      async (logAttempt) => {
        logAttempt.identifiers = {
          client_guid: this._clientContext?.activeGuid(),
          case_id: this._caseId,
          option_id: optionId,
        };
        logAttempt.stage = 'save';
        const caseId = this._requiredCaseId();
        const clientGuid = this._clientContext?.activeGuid();
        const previous = this._options().find(
          (option) => option.id === optionId,
        );
        return this._mutate(
          optionId,
          () =>
            logAttempt.request(this._api.updateOption(caseId, optionId, value)),
          (updated) => {
            logAttempt.completedCount = 1;
            if (
              !clientGuid ||
              !previous ||
              !value.statusCode ||
              previous.statusCode === updated.statusCode
            ) {
              return;
            }
            this._analytics.optionStatusChanged({
              clientGuid,
              caseId,
              optionId,
              optionType: updated.optionTypeCode,
              fromStatus: previous.statusCode,
              toStatus: updated.statusCode,
            });
          },
        );
      },
    );
  }

  async updateStatus(optionId: string, statusCode: string): Promise<void> {
    await this.update(optionId, { statusCode });
  }

  private async _loadReferences(): Promise<void> {
    const clientId = this._clientContext?.clientId() ?? null;
    if (this._referencesLoaded && this._referenceClientId === clientId) return;
    const references = await firstValueFrom(
      this._references.loadReferenceData(clientId),
    );
    if ((this._clientContext?.clientId() ?? null) !== clientId) return;
    this._optionTypes.set([...references.optionTypes]);
    this._optionStatuses.set([...references.optionStatuses]);
    this._optionTitles.set([...references.optionTitles]);
    this._allowedTransitions.set(references.allowedTransitions);
    this._referencesLoaded = true;
    this._referenceClientId = clientId;
  }

  private async _mutate<T>(
    optionId: string,
    request: () => Observable<T>,
    afterSuccess?: (result: T) => void,
  ): Promise<T> {
    this._setSaving(optionId, true);
    try {
      const result = await firstValueFrom(request());
      afterSuccess?.(result);
      if (this._caseId) await this.load(this._caseId);
      return result;
    } finally {
      this._setSaving(optionId, false);
    }
  }

  private _requiredCaseId(): string {
    if (!this._caseId) throw new Error('Case context is unavailable.');
    return this._caseId;
  }

  private _label(
    values: readonly OptionReferenceValue[],
    code: string,
  ): string {
    return (
      values.find((value) => value.code === code)?.label ??
      code
        .toLowerCase()
        .split('_')
        .filter(Boolean)
        .map((part) => part[0].toUpperCase() + part.slice(1))
        .join(' ')
    );
  }

  private _setSaving(optionId: string, saving: boolean): void {
    this._savingIds.update((current) => {
      const next = new Set(current);
      if (saving) next.add(optionId);
      else next.delete(optionId);
      return next;
    });
  }

  private _message(error: unknown): string {
    return error instanceof Error
      ? error.message
      : "Options couldn't be loaded right now.";
  }
}
